import { useEffect, useState } from "react";
import { api, errMsg, fmtDate, money } from "@/lib/api";
import { StatusChip } from "@/components/StatusChip";
import { Button } from "@/components/ui/button";
import { CreditCard, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function PortalBilling() {
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState("");

  const load = () => api.get("/portal/overview").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!data) return <div className="py-20 text-center text-slate-400 text-sm">Loading billing…</div>;

  const payPayPal = async (inv) => {
    setBusyId(inv.id);
    try {
      const order = await api.post("/portal/billing/paypal/create-order", { invoice_id: inv.id });
      if (order.data.mock) {
        await api.post("/portal/billing/paypal/capture", { invoice_id: inv.id, order_id: order.data.order_id });
        toast.success("Payment complete (demo mode — no real charge)");
      } else {
        toast.info("Redirecting to PayPal checkout…");
        await api.post("/portal/billing/paypal/capture", { invoice_id: inv.id, order_id: order.data.order_id });
      }
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusyId(""); }
  };

  const pending = data.invoices.filter((i) => i.status === "pending");
  const history = data.invoices.filter((i) => i.status !== "pending");

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="portal-billing">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Billing</h1>
        <p className="text-slate-600 mt-1">Simple, transparent pricing. No hidden charges, ever.</p>
      </div>

      {pending.length === 0 && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 flex items-center gap-3" data-testid="billing-all-clear">
          <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
          <div className="text-sm text-emerald-800">You're all paid up. Nothing is due right now.</div>
        </div>
      )}

      {pending.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-heading font-semibold text-slate-800">Due now</h2>
          {pending.map((inv) => (
            <div key={inv.id} className="rounded-xl border border-slate-200 bg-white p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`invoice-card-${inv.id.slice(0, 8)}`}>
              <div>
                <div className="font-medium text-slate-900">{inv.label}</div>
                <div className="text-sm text-slate-500">{fmtDate(inv.created_at)} · <StatusChip status={inv.status} testId="invoice-status-pill" /></div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-2xl font-bold font-heading text-slate-900">{money(inv.amount)}</div>
                <Button onClick={() => payPayPal(inv)} disabled={busyId === inv.id} data-testid={`paypal-pay-btn-${inv.id.slice(0, 8)}`}
                  className="bg-[#FFC439] hover:bg-[#f2b621] text-slate-900 font-semibold">
                  <CreditCard className="h-4 w-4 mr-2" /> {busyId === inv.id ? "Processing…" : "Pay with PayPal"}
                </Button>
              </div>
            </div>
          ))}
          <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600">
            Prefer CashApp? Send the exact amount to <span className="font-mono font-semibold text-slate-800">{data.fees.cashapp_handle || "our CashApp"}</span> and include your name — your case manager will apply it to your account within one business day.
          </div>
        </div>
      )}

      <div>
        <h2 className="font-heading font-semibold text-slate-800 mb-3">Payment history</h2>
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200">
              {["Description", "Amount", "Status", "Paid via", "Date"].map((h) => (
                <th key={h} className="text-left py-2.5 px-4 text-xs font-mono uppercase tracking-[0.18em] text-slate-500">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {history.map((i) => (
                <tr key={i.id} className="border-b border-slate-100">
                  <td className="py-2.5 px-4 text-slate-800">{i.label}</td>
                  <td className="py-2.5 px-4 text-slate-700">{money(i.amount)}</td>
                  <td className="py-2.5 px-4"><StatusChip status={i.status} testId="invoice-status-pill" /></td>
                  <td className="py-2.5 px-4 text-slate-600 capitalize">{i.method || "—"}</td>
                  <td className="py-2.5 px-4 text-slate-500">{fmtDate(i.paid_at || i.created_at)}</td>
                </tr>
              ))}
              {history.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">No payments yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-slate-400 leading-relaxed">
        Your plan: {money(data.fees.setup)} one-time setup{data.fees.setup_required ? " (required before work begins)" : ""} + {money(data.fees.monthly)}/month ongoing service. Cancel any time; you may cancel this agreement within 3 business days of signing under the Credit Repair Organizations Act.
      </p>
    </div>
  );
}
